import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiResponse } from '@shared/models/api-response.model';
import {
  AREA_FILTER_MODE,
  AppliedFilter,
  AppliedFilterApiItem,
  INCIDENT_SCOPE,
  IncidentScope,
  MeScope,
  NO_WAREHOUSE_LABEL,
  SCOPE_FAILURE,
  SCOPE_HTTP_STATUS,
  ScopeAreaApiItem,
  ScopeFailure,
  ScopeLoadResult,
  ScopeWarehouseApiItem,
  ScopeWarehouseArea,
} from '../models/scope.model';
import {
  toAppliedFilter,
  toScopeArea,
  toScopeAreas,
  toScopeWarehouse,
} from '../utilities/scope-params.util';

interface WarehouseAreaApiItem extends ScopeAreaApiItem {
  active?: boolean | null;
  is_mine?: boolean | null;
}

interface MeScopeApiItem {
  warehouse?: ScopeWarehouseApiItem | null;
  areas?: ScopeAreaApiItem[] | null;
  warehouse_areas?: WarehouseAreaApiItem[] | null;
  default_selection?: AppliedFilterApiItem | null;
  incident_scope?: string | null;
  can_create_schedule?: boolean | null;
}

const INCIDENT_SCOPE_VALUES: readonly string[] = Object.values(INCIDENT_SCOPE);

@Injectable({ providedIn: 'root' })
export class ScopeService {
  //* Services
  private readonly _http = inject(HttpClient);

  //* Variables
  private readonly _baseUrl: string = `${environment.urlBase}/api/v1/me/scope`;

  //* Public Methods
  public getScope(): Observable<ScopeLoadResult> {
    return this._http.get<ApiResponse<MeScopeApiItem>>(this._baseUrl).pipe(
      map((res) => this._toResult(res?.data ?? null)),
      catchError((err: HttpErrorResponse) => of(this._toFailure(this._toFailureCode(err.status)))),
    );
  }

  //* Private Methods
  private _toResult(item: MeScopeApiItem | null): ScopeLoadResult {
    if (!item) return this._toFailure(SCOPE_FAILURE.UNAVAILABLE);

    return { scope: this._toMeScope(item), failure: null };
  }

  private _toFailure(failure: ScopeFailure): ScopeLoadResult {
    return { scope: null, failure };
  }

  private _toFailureCode(status: number): ScopeFailure {
    switch (status) {
      case SCOPE_HTTP_STATUS.UNAUTHORIZED:
        return SCOPE_FAILURE.UNAUTHORIZED;
      case SCOPE_HTTP_STATUS.FORBIDDEN:
        return SCOPE_FAILURE.FORBIDDEN;
      default:
        return SCOPE_FAILURE.UNAVAILABLE;
    }
  }

  private _toMeScope(item: MeScopeApiItem): MeScope {
    const warehouse = toScopeWarehouse(item.warehouse);

    return {
      warehouse,
      areas: warehouse ? toScopeAreas(item.areas) : [],
      warehouseAreas: warehouse ? this._toWarehouseAreas(item.warehouse_areas) : [],
      defaultSelection: this._toDefaultSelection(item.default_selection, warehouse !== null),
      incidentScope: this._toIncidentScope(item.incident_scope),
      canCreateSchedule: item.can_create_schedule ?? false,
    };
  }

  private _toWarehouseAreas(
    items: WarehouseAreaApiItem[] | null | undefined,
  ): ScopeWarehouseArea[] {
    if (!Array.isArray(items)) return [];

    return items.flatMap((item) => {
      const area = toScopeArea(item);

      return area ? [{ ...area, active: item.active ?? true, isMine: item.is_mine ?? false }] : [];
    });
  }

  private _toDefaultSelection(
    item: AppliedFilterApiItem | null | undefined,
    hasWarehouse: boolean,
  ): AppliedFilter {
    const filter = toAppliedFilter(item);

    if (!hasWarehouse) {
      return {
        mode: AREA_FILTER_MODE.DEFAULT,
        warehouse: null,
        areas: [],
        label: NO_WAREHOUSE_LABEL,
      };
    }

    return filter ?? { mode: AREA_FILTER_MODE.DEFAULT, warehouse: null, areas: [], label: '' };
  }

  private _toIncidentScope(value: string | null | undefined): IncidentScope | null {
    if (value && INCIDENT_SCOPE_VALUES.includes(value)) return value as IncidentScope;

    return null;
  }
}
